/* global Excel */

/**
 * Shared builder for "METRICS table" worksheets — used by the Getting Started
 * demo sheet and the screener's insert-results feature.
 *
 * Layout (the input-driven pattern):
 *   - Column A: cert numbers as plain values.
 *   - Info columns next: each cell is =SHEETYBANKS.INFO($A<row>, <col>$1), so
 *     the header cell is a live input — type "city" over "name" and the whole
 *     column repoints.
 *   - Metric columns last: a two-row header (field names, then quarters where
 *     a blank means "latest") and ONE spilling METRICS formula in the first
 *     metric cell that fills the whole grid.
 */

export interface MetricColumn {
  field: string; // FDIC field name, e.g. 'ASSET', 'ROA'
  quarter?: string; // e.g. '2025Q4'; omitted = latest available
  numberFormat?: string; // optional Excel format for the column, e.g. '#,##0'
}

export interface MetricsSheetSpec {
  baseName: string; // worksheet name; " 2", " 3", … appended if taken
  certs: number[];
  infoColumns: string[]; // INFO fields, e.g. ['Name', 'City']
  metricColumns: MetricColumn[];
}

// 0 → A, 1 → B, …, 26 → AA
export function colLetter(index: number): string {
  let letter = "";
  let n = index;
  do {
    letter = String.fromCharCode(65 + (n % 26)) + letter;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letter;
}

export async function insertMetricsSheet(spec: MetricsSheetSpec): Promise<string> {
  if (spec.certs.length === 0) {
    throw new Error("No certs to insert");
  }

  let sheetName = "";
  await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    const existing = worksheets.items.map((s) => s.name);
    sheetName = spec.baseName;
    for (let i = 2; existing.indexOf(sheetName) !== -1; i++) {
      sheetName = `${spec.baseName} ${i}`;
    }

    const sheet = worksheets.add(sheetName);

    const firstDataRow = 3; // rows 1–2 are the field/quarter header
    const lastDataRow = firstDataRow + spec.certs.length - 1;
    const metricStartCol = 1 + spec.infoColumns.length; // 0-based; A is certs
    const lastCol = metricStartCol + spec.metricColumns.length - 1;

    // Header row 1: Cert, info fields, metric fields.
    const header = ["Cert", ...spec.infoColumns, ...spec.metricColumns.map((m) => m.field)];
    sheet.getRange(`A1:${colLetter(header.length - 1)}1`).values = [header];

    // Header row 2: quarters under the metric columns (blank = latest).
    if (spec.metricColumns.length > 0) {
      sheet.getRange(`${colLetter(metricStartCol)}2:${colLetter(lastCol)}2`).values = [
        spec.metricColumns.map((m) => m.quarter ?? ""),
      ];
    }

    // Cert values down column A.
    sheet.getRange(`A${firstDataRow}:A${lastDataRow}`).values = spec.certs.map((c) => [c]);

    // Info columns: one INFO formula per cell, reading its field from the header.
    spec.infoColumns.forEach((_field, i) => {
      const col = colLetter(1 + i);
      sheet.getRange(`${col}${firstDataRow}:${col}${lastDataRow}`).formulas = spec.certs.map(
        (_cert, row) => [`=SHEETYBANKS.INFO($A${firstDataRow + row}, ${col}$1)`]
      );
    });

    // Metric columns: one METRICS formula spills the whole grid.
    if (spec.metricColumns.length > 0) {
      const fieldsRange = `${colLetter(metricStartCol)}1:${colLetter(lastCol)}1`;
      const quartersRange = `${colLetter(metricStartCol)}2:${colLetter(lastCol)}2`;
      sheet.getRange(`${colLetter(metricStartCol)}${firstDataRow}`).formulas = [
        [
          `=SHEETYBANKS.METRICS(A${firstDataRow}:A${lastDataRow}, ${fieldsRange}, ${quartersRange})`,
        ],
      ];

      spec.metricColumns.forEach((m, i) => {
        if (m.numberFormat) {
          const col = colLetter(metricStartCol + i);
          sheet.getRange(`${col}${firstDataRow}:${col}${lastDataRow}`).numberFormat =
            spec.certs.map(() => [m.numberFormat as string]);
        }
      });
    }

    sheet.getRange(`A1:${colLetter(lastCol)}2`).format.font.bold = true;
    sheet.getRange(`A${firstDataRow}:A${lastDataRow}`).format.font.bold = true;
    sheet.getUsedRange().format.autofitColumns();
    sheet.activate();
    await context.sync();
  });

  return sheetName;
}
